import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { triggerBlobDownload, blobErrorMessage } from "src/utils/download_blob";

// Initial state
const initialState = {
    listData: null,
    page: 0,
    pageCount: 0,
    totalCount: 0,
    loading: false,
    summaryData: null,
    detailData: null,
    detailLoading: false,
    updateFulfillmentSuccess: false,
    updateShippingSuccess: false,
    error: null,

    // Export to Excel
    exportLoading: false,
    exportError: null,
};

// Slice
const treasureBoxPlaysSlice = createSlice({
    name: "treasureBoxPlays",
    initialState,
    reducers: {
        // Export to Excel
        doExportTreasureBoxPlaysRequest: (state) => {
            state.exportLoading = true;
            state.exportError = null;
        },
        doExportTreasureBoxPlaysSuccess: (state) => {
            state.exportLoading = false;
        },
        doExportTreasureBoxPlaysFail: (state, action) => {
            state.exportLoading = false;
            state.exportError = action.payload || "Export failed. Please try again.";
        },

        // Get treasure box plays list
        getTreasureBoxPlaysRequest: (state) => {
            state.loading = true;
            state.error = null;
        },
        getTreasureBoxPlaysSuccess: (state, action) => {
            state.loading = false;
            state.listData = action.payload.data;
            state.page = action.payload.current_page;
            state.pageCount = action.payload.page_count;
            state.totalCount = action.payload.total_count;
        },
        getTreasureBoxPlaysFail: (state, action) => {
            state.loading = false;
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
            state.totalCount = 0;
            state.error = action.payload;
        },

        // Get treasure box plays summary (KPI aggregates)
        getTreasureBoxPlaysSummarySuccess: (state, action) => {
            state.summaryData = action.payload?.data;
        },
        getTreasureBoxPlaysSummaryFail: (state) => {
            state.summaryData = null;
        },

        // Get treasure box play detail
        getTreasureBoxPlayDetailRequest: (state) => {
            state.detailLoading = true;
            state.error = null;
        },
        getTreasureBoxPlayDetailSuccess: (state, action) => {
            state.detailLoading = false;
            state.detailData = action.payload;
        },
        getTreasureBoxPlayDetailFail: (state, action) => {
            state.detailLoading = false;
            state.detailData = null;
            state.error = action.payload;
        },

        // Update fulfillment status
        updateFulfillmentStatusRequest: (state) => {
            state.loading = true;
            state.updateFulfillmentSuccess = false;
            state.error = null;
        },
        updateFulfillmentStatusSuccess: (state) => {
            state.loading = false;
            state.updateFulfillmentSuccess = true;
        },
        updateFulfillmentStatusFail: (state, action) => {
            state.loading = false;
            state.updateFulfillmentSuccess = false;
            state.error = action.payload;
        },

        // Reset state
        resetTreasureBoxPlaysState: (state) => {
            return initialState;
        },

        // Reset update success flag
        resetUpdateFulfillmentSuccess: (state) => {
            state.updateFulfillmentSuccess = false;
        },

        // Clear detail data
        clearTreasureBoxPlayDetail: (state) => {
            state.detailData = null;
        },

        // Update shipping details
        updateShippingRequest: (state) => {
            state.loading = true;
            state.updateShippingSuccess = false;
            state.error = null;
        },
        updateShippingSuccess: (state) => {
            state.loading = false;
            state.updateShippingSuccess = true;
        },
        updateShippingFail: (state, action) => {
            state.loading = false;
            state.updateShippingSuccess = false;
            state.error = action.payload;
        },
        resetUpdateShippingSuccess: (state) => {
            state.updateShippingSuccess = false;
        },
    },
});

// Export actions
export const {
    getTreasureBoxPlaysRequest,
    getTreasureBoxPlaysSuccess,
    getTreasureBoxPlaysFail,
    getTreasureBoxPlaysSummarySuccess,
    getTreasureBoxPlaysSummaryFail,
    getTreasureBoxPlayDetailRequest,
    getTreasureBoxPlayDetailSuccess,
    getTreasureBoxPlayDetailFail,
    updateFulfillmentStatusRequest,
    updateFulfillmentStatusSuccess,
    updateFulfillmentStatusFail,
    resetTreasureBoxPlaysState,
    resetUpdateFulfillmentSuccess,
    clearTreasureBoxPlayDetail,
    updateShippingRequest,
    updateShippingSuccess,
    updateShippingFail,
    resetUpdateShippingSuccess,
    doExportTreasureBoxPlaysRequest,
    doExportTreasureBoxPlaysSuccess,
    doExportTreasureBoxPlaysFail,
} = treasureBoxPlaysSlice.actions;

// Thunks

/**
 * Fetch treasure box plays list with filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.size - Items per page
 * @param {number} params.treasure_box_id - Filter by treasure box ID
 * @param {number} params.customer_id - Filter by customer ID
 * @param {string} params.transaction_status - Filter by transaction status (success/failed/refunded)
 * @param {string} params.fulfillment_status - Filter by fulfillment status (pending/shipped/delivered/canceled)
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {string} params.search - Search by customer name/email/mobile or treasure box name
 */
export const doGetTreasureBoxPlaysList = (params) => async (dispatch) => {
    dispatch(getTreasureBoxPlaysRequest());

    try {
        const token = localStorage.getItem("token");
        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params: {
                page: params.page || 1,
                size: params.size || 20,
                ...params,
            },
        };

        // Remove undefined/null/empty parameters
        Object.keys(config.params).forEach((key) => {
            if (
                config.params[key] === undefined ||
                config.params[key] === null ||
                config.params[key] === ""
            ) {
                delete config.params[key];
            }
        });

        const response = await axios.get(
            `/api/admin/treasure-box-plays`,
            config
        );

        dispatch(
            getTreasureBoxPlaysSuccess({
                data: response.data.data,
                current_page: response.data.current_page,
                page_count: response.data.page_count,
                total_count: response.data.total_count,
            })
        );
       
    } catch (error) {
        console.error("Error fetching treasure box plays:", error);
        dispatch(
            getTreasureBoxPlaysFail(
                error.response?.data?.message || "Failed to fetch treasure box plays"
            )
        );
    }
};

/**
 * Fetch treasure box plays summary (KPI aggregates)
 */
export const doGetTreasureBoxPlaysSummary = (params = {}) => async (dispatch) => {
    try {
        const token = localStorage.getItem("token");
        // The summary takes the same filters as the list. Without them the stat
        // cards aggregate the whole table while the table below shows a filtered
        // subset, which is what made "Total Plays 4" sit above a single row.
        // page/size are dropped: they mean nothing to an aggregate and the
        // endpoint's schema rejects unknown keys.
        const { page, size, limit, ...filters } = params;
        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params: { ...filters },
        };

        Object.keys(config.params).forEach((key) => {
            if (
                config.params[key] === undefined ||
                config.params[key] === null ||
                config.params[key] === ""
            ) {
                delete config.params[key];
            }
        });

        const response = await axios.get(
            `/api/admin/treasure-box-plays/summary`,
            config
        );

        dispatch(getTreasureBoxPlaysSummarySuccess(response.data));
    } catch (error) {
        console.error("Error fetching treasure box plays summary:", error);
        dispatch(getTreasureBoxPlaysSummaryFail());
    }
};

/**
 * Fetch treasure box play detail
 * @param {number} playId - Play ID
 */
export const doGetTreasureBoxPlayDetail = (playId) => async (dispatch) => {
    dispatch(getTreasureBoxPlayDetailRequest());

    try {
        const token = localStorage.getItem("token");
        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };

        const response = await axios.get(
            `/api/admin/treasure-box-plays/${playId}`,
            config
        );

        dispatch(getTreasureBoxPlayDetailSuccess(response.data));
    } catch (error) {
        console.error("Error fetching treasure box play detail:", error);
        dispatch(
            getTreasureBoxPlayDetailFail(
                error.response?.data?.message || "Failed to fetch play detail"
            )
        );
    }
};

/**
 * Update fulfillment status
 * @param {number} playId - Play ID
 * @param {Object} data - Update data
 * @param {string} data.fulfillment_status - New fulfillment status
 * @param {string} data.tracking_number - Tracking number (required if status is 'shipped')
 * @param {string} data.shipping_carrier - Shipping carrier (required if status is 'shipped')
 * @param {string} data.notes - Admin notes (optional)
 */
export const doUpdateFulfillmentStatus = (playId, data) => async (dispatch) => {
    dispatch(updateFulfillmentStatusRequest());

    try {
        const token = localStorage.getItem("token");
        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };

        const response = await axios.put(
            `/api/admin/treasure-box-plays/${playId}/fulfillment-status`,
            data,
            config
        );

        dispatch(updateFulfillmentStatusSuccess());
    } catch (error) {
        console.error("Error updating fulfillment status:", error);
        dispatch(
            updateFulfillmentStatusFail(
                error.response?.data?.message || "Failed to update fulfillment status"
            )
        );
    }
};

/**
 * Update shipping details
 * @param {number} playId - Play ID
 * @param {Object} data - Shipping data
 * @param {string} data.shipping_name - Recipient name
 * @param {string} data.shipping_phone - Phone number
 * @param {string} data.shipping_address - Address
 * @param {string} data.shipping_city - City
 * @param {string} data.shipping_state - State
 * @param {string} data.shipping_postal_code - Postal code
 * @param {string} data.shipping_country - Country
 */
export const doUpdateShipping = (playId, data) => async (dispatch) => {
    dispatch(updateShippingRequest());

    try {
        const token = localStorage.getItem("token");
        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };

        const response = await axios.put(
            `/api/admin/treasure-box-plays/${playId}/shipping`,
            data,
            config
        );

        dispatch(updateShippingSuccess());
    } catch (error) {
        console.error("Error updating shipping:", error);
        dispatch(
            updateShippingFail(
                error.response?.data?.message || "Failed to update shipping details"
            )
        );
    }
};

// Selectors
export const getTreasureBoxPlaysList = (state) => state.treasureBoxPlays.listData;
export const getTreasureBoxPlaysPage = (state) => state.treasureBoxPlays.page;
export const getTreasureBoxPlaysPageCount = (state) => state.treasureBoxPlays.pageCount;
export const getTreasureBoxPlaysTotalCount = (state) => state.treasureBoxPlays.totalCount;
export const getTreasureBoxPlaysLoading = (state) => state.treasureBoxPlays.loading;
export const getTreasureBoxPlaysSummary = (state) => state.treasureBoxPlays.summaryData;
export const getTreasureBoxPlayDetail = (state) => state.treasureBoxPlays.detailData;
export const getTreasureBoxPlayDetailLoading = (state) => state.treasureBoxPlays.detailLoading;
export const getUpdateFulfillmentSuccess = (state) => state.treasureBoxPlays.updateFulfillmentSuccess;
export const getUpdateShippingSuccess = (state) => state.treasureBoxPlays.updateShippingSuccess;
export const getTreasureBoxPlaysError = (state) => state.treasureBoxPlays.error;

export default treasureBoxPlaysSlice.reducer;

// Export the current filter set (ALL pages, not just the visible one) as an .xlsx.
export const doExportTreasureBoxPlays = (param = {}) => async (dispatch) => {
    dispatch(doExportTreasureBoxPlaysRequest());
    try {
        const params = { ...param };
        Object.keys(params).forEach((key) => {
            if (params[key] === undefined || params[key] === null || params[key] === "") delete params[key];
        });
        const response = await axios.get("/api/admin/treasure-box-plays/export", { params, responseType: "blob" });
        triggerBlobDownload(response, "Mystery_Draw_Orders");
        dispatch(doExportTreasureBoxPlaysSuccess());
    } catch (error) {
        dispatch(doExportTreasureBoxPlaysFail(await blobErrorMessage(error)));
    }
};

export const getExportTreasureBoxPlaysLoading = (state) => state.treasureBoxPlays?.exportLoading || false;
export const getExportTreasureBoxPlaysError = (state) => state.treasureBoxPlays?.exportError || null;
